import React, { useCallback, useEffect, useState } from 'react'
import ReportsAndStatisticsView from '../../views/reports-and-statistics/reports-and-statistics.view'
import { useHistory, useLocation } from 'react-router-dom';
import { ClientsService, LocalStorageService, UsageService } from '../../../services';
import moment from 'moment';
import { ModalUtil } from '../../pures';
import { useTranslation } from 'react-i18next';
import { useGlobalState } from '../../../GlobalState';

const url = process.env.REACT_APP_ETICKET_API;
const localStorageService = new LocalStorageService();
const clientsService = new ClientsService(url)

// La sesión se lee en cada handler (no en el scope del módulo): este módulo se
// importa de forma eager desde routes/index.js, antes de que exista el login.
const getToken = () => localStorageService.getLocalStorageItem("token")?.idToken
const usageService = new UsageService(url)

const estimatePeriod = (startDate, endDate) => {
    startDate = moment(startDate)
    endDate = moment(endDate)

    // if the dates are the same
    if (startDate.isSame(endDate, 'day')) return "day"
    // if the difference is between 7 or less days
    if (endDate.diff(startDate, 'day') <= 7) return "week"
    // if the dates are within the same month
    if (startDate.isSame(endDate, 'month')) return "month"
    // if the dates are in different months, the period is "year"
    return "year"
}

function ReportsAndStatisticsAction() {
    const history = useHistory();
    const location = useLocation();

    const { t } = useTranslation();
    const modalUtil = new ModalUtil()
    const { botConfig } = useGlobalState()

    const [isLoading, setIsLoading] = useState(true)
    const [data, setData] = useState({})
    const [sentimentData, setSentimentData] = useState(null)
    const [sentimentPeriod, setSentimentPeriod] = useState(null)

    const [dates, setDates] = useState({
        startDate: moment().subtract(1, 'year').toDate(),
        endDate: moment().toDate()
    })
    const [periodType, setPeriodType] = useState("year") // "year" | "month" | "week" | "day"

    const handleChangeDate = (event, field) => {
        setDates({ ...dates, [field]: event.target.value });
    }

    const loadData = async (firstRender) => {
        setIsLoading(true)

        try {
            let startDate = dates.startDate
            let endDate = dates.endDate

            if (firstRender) {
                if (location.search) {
                    const searchParams = new URLSearchParams(location.search);
                    startDate = moment(searchParams.get('startDate')).toDate()
                    endDate = moment(searchParams.get('endDate')).toDate()
                    setDates({ startDate, endDate })
                }
            } else {
                const searchParams = new URLSearchParams();
                startDate = moment(dates.startDate).toDate()
                endDate = moment(dates.endDate).toDate()

                searchParams.append('startDate', moment(startDate).format('YYYY-MM-DD'));
                searchParams.append('endDate', moment(endDate).format('YYYY-MM-DD'));
                history.replace({ pathname: '/reports', search: searchParams.toString() })
            }

            const period = estimatePeriod(startDate, endDate)
            // Sentiment report is month-scoped: use the range's final month.
            const sentimentYear = moment(endDate).year()
            const sentimentMonth = moment(endDate).month() + 1
            const token = getToken()
            const [dashboardData, sentimentReport] = await Promise.all([
                clientsService.getReportsAndStatisticsDashboard(token, startDate, endDate, period),
                usageService.getSentimentAnalysisReport(sentimentYear, sentimentMonth, token, botConfig?.id)
            ])
            setPeriodType(period)
            setData(dashboardData)
            setSentimentData(sentimentReport)
            setSentimentPeriod(sentimentYear * 100 + sentimentMonth)
            setIsLoading(false)
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        loadData(true);
    }, [])

    // Navega al Historial con la Búsqueda Avanzada preseleccionada: mes completo del
    // periodo del reporte de sentimiento y el estado de la sección de la gráfica pulsada.
    // useCallback: mantiene la referencia estable entre renders para que AttentionStatusGraph
    // no reciba un onEvents nuevo en cada render (echarts-for-react recrea el chart si cambia).
    const handleAttentionStatusClick = useCallback((status) => {
        history.push({
            pathname: '/history',
            state: { advancedSearch: { historyStatus: status, period: sentimentPeriod } }
        })
    }, [history, sentimentPeriod])

    return (
        <ReportsAndStatisticsView
            isLoading={isLoading}
            dates={dates}
            handleChangeDate={handleChangeDate}
            handleSearch={() => loadData(false)}
            periodType={periodType}
            data={data}
            sentimentData={sentimentData}
            onAttentionStatusClick={handleAttentionStatusClick}
        />
    )
}

export default ReportsAndStatisticsAction